import { Camera, Upload, X } from 'lucide-react'
import React, { useRef, useState } from 'react'

import { toast } from '@/hooks/use-toast'

import NextImage from '@/components/NextImage'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { useAuth } from '@/contexts/auth-context'
import { useChat } from '@/contexts/chat-context'
import { generalChatService } from '@/services/general-chat.service'
import uploadService from '@/services/upload.service'

import { Conversation, ParticipantRole } from '@/types/chat.type'

interface UpdateConversationAvatarDialogProps {
  conversation: Conversation
  isOpen: boolean
  onClose: () => void
}

export const UpdateConversationAvatarDialog: React.FC<UpdateConversationAvatarDialogProps> = ({
  conversation,
  isOpen,
  onClose,
}) => {
  const { user } = useAuth()
  const { updateConversation } = useChat()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const currentUserParticipant = conversation?.participants?.find(p => p.user.id === user?.id)
  const isOwner = currentUserParticipant?.role === ParticipantRole.OWNER

  if (!isOwner) {
    return null
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid file type',
          description: 'Please select an image file.',
          variant: 'destructive',
        })
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please select an image smaller than 5MB.',
          variant: 'destructive',
        })
        return
      }

      setSelectedFile(file)

      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    try {
      const uploadResult = await uploadService.uploadImage({ image: selectedFile })

      await generalChatService.updateConversation(conversation.id, {
        avatar: uploadResult.url,
      })

      updateConversation(conversation.id, { avatar: uploadResult.url })

      toast({
        title: 'Avatar updated',
        description: 'The conversation avatar has been updated successfully.',
      })

      handleClose()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating conversation avatar:', error)
      toast({
        title: 'Error',
        description: 'Failed to update conversation avatar. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onClose()
  }

  const handleRemoveAvatar = async () => {
    setIsUploading(true)
    try {
      await generalChatService.updateConversation(conversation.id, {
        avatar: undefined,
      })

      updateConversation(conversation.id, { avatar: null })

      toast({
        title: 'Avatar removed',
        description: 'The conversation avatar has been removed.',
      })

      handleClose()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error removing conversation avatar:', error)
      toast({
        title: 'Error',
        description: 'Failed to remove conversation avatar. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Conversation Avatar</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Avatar Display */}
          <div className="flex justify-center">
            <div className="relative">
              {previewUrl ? (
                <NextImage
                  src={previewUrl}
                  alt="Preview"
                  width={90}
                  height={90}
                  className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 overflow-hidden"
                />
              ) : conversation.avatar ? (
                <NextImage
                  src={conversation.avatar}
                  alt="Current avatar"
                  width={90}
                  height={90}
                  className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 overflow-hidden"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-200">
                  <Camera className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </div>
          </div>

          {/* File Input */}
          <div className="space-y-2">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="w-full"
              disabled={isUploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              {selectedFile ? 'Change Image' : 'Select Image'}
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {selectedFile && (
              <Button onClick={handleUpload} disabled={isUploading} className="flex-1">
                {isUploading ? 'Uploading...' : 'Update Avatar'}
              </Button>
            )}

            {conversation.avatar && (
              <Button variant="outline" onClick={handleRemoveAvatar} disabled={isUploading} className="flex-1">
                <X className="w-4 h-4 mr-2" />
                Remove Avatar
              </Button>
            )}
          </div>

          {/* Cancel Button */}
          <Button variant="ghost" onClick={handleClose} disabled={isUploading} className="w-full">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
